/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import request from 'apmui/util/request';
import XML from 'apmui/util/XML';

import VdiClientInfo, { VDI_CLIENT, LaunchFunction } from './VdiClientInfo';
import VdiError, { VdiErrorType } from './VdiError';
import CitrixModel from './CitrixModel';
import createFrame, { getDocument } from './createFrame';
import { getCookie, deleteCookie } from 'apmui/common/Cookie';
import LaunchType from 'apmui/resource/LaunchType';
import userAgent, { OS } from 'apmui/util/UserAgent';

const DEFAULT_DOWNLOAD_URL = 'https://receiver.citrix.com';
const NETWORK_REQUEST_TIMEOUT = 15000;

/* Declare globals required for HTML5 client */
declare global {
    /* eslint-disable-next-line @typescript-eslint/interface-name-prefix */
    interface Window {
        /* Launch parameters for Citrix HTML5 client (webtop window) */
        html5LaunchData: Record<number, Record<string, string>>;
        /* This flag tells Citrix client to try reading launch parameters (HTML5 client window) */
        icaDwldComplete: boolean;
    }
}
window.html5LaunchData = {};

let clientsCache: VdiClientInfo | null = null;

async function getClients() {
    if (!clientsCache) {
        const { nativeClientDownloadUrl, html5ClientBaseUrl } = await fetchCitrixPackages();
        clientsCache = {
            [VDI_CLIENT.NATIVE]: {
                name: 'Citrix Receiver',
                downloadUrl: nativeClientDownloadUrl || DEFAULT_DOWNLOAD_URL,
                launch: launchNativeClient as LaunchFunction,
            },
        };

        if (html5ClientBaseUrl) {
            clientsCache[VDI_CLIENT.HTML5] = {
                name: 'HTML5',
                launch: (resource, launchType) =>
                    launchHtml5Client(resource as CitrixModel, html5ClientBaseUrl, launchType),
            };
        }
    }
    return clientsCache;
}

interface ICitrixPackageInfo {
    nativeClientDownloadUrl: string;
    html5ClientBaseUrl?: string;
}

/**
 * Get URLs to download Citrix clients.
 * BIGIP may have special configuration for this.
 * The configuration is sent to client in XML by request to /pre/package_info.php
 * Example of XML response:
 *
 * <?xml version="1.0" encoding="utf-8"?>
 * <info>
 * <packages>
 *   <package>
 *     <os>windows</os>
 *     <url>https://www.citrix.com/downloads/citrix-receiver/windows-ltsr/receiver-for-windows-ltsr_4_9_3000.html</url>
 *     <min-version>4.4.2000</min-version>
 *   </package>
 * </packages>
 * <default>
 *   <url>receiver.citrix.com</url>
 * </default>
 * </info>
 *
 * Only <url> tag is significant; <default> clause is used in most cases.
 * On Windows clients, <package> clause with '<os>windows</os>' tag may be used.
 * If URL contains 'html5', it is used as base URL for HTML5 client.
 */
async function fetchCitrixPackages(): Promise<ICitrixPackageInfo> {
    let windowsUri: string | undefined;
    let defaultUri = DEFAULT_DOWNLOAD_URL;

    function isHtml5Uri(uri?: string): uri is string {
        return typeof uri === 'string' && uri.toLowerCase().indexOf('html5') !== -1;
    }

    function getDownloadUrl() {
        let url = defaultUri;
        if (userAgent.os === OS.WINDOWS && windowsUri && !isHtml5Uri(windowsUri)) {
            url = windowsUri;
        }
        // package URL of form "citrix.com/download" won't work with
        // window.open(). Need to append "http://"
        if (url.charAt(0) !== '/' && url.indexOf('http://') === -1 && url.indexOf('https://') === -1) {
            return 'http://' + url;
        }
        return url;
    }

    try {
        const xhr = await request('/pre/package_info.php', { timeout: NETWORK_REQUEST_TIMEOUT });
        const xml = XML.responseXML(xhr);
        const packageInfo = xml.documentElement;

        if (!packageInfo) {
            throw new Error('Citrix: Empty client package info received');
        }

        const defaultNode = packageInfo.querySelector('default');
        if (defaultNode) {
            defaultUri = XML.getValueOfChildByTag(defaultNode, 'url', defaultUri);
        }

        const packageNodes = packageInfo.querySelectorAll('packages > package');

        for (let i = 0; i < packageNodes.length; i++) {
            if (XML.getValueOfChildByTag(packageNodes[i], 'os') !== 'windows') {
                continue;
            }
            // ignore unused 'min-version' tag
            const uri = XML.getValueOfChildByTag(packageNodes[i], 'url');
            if (uri) {
                windowsUri = uri;
            }
        }
    } catch (err) {
        console.error(err);
    }

    let html5ClientBaseUrl = '';
    if (isHtml5Uri(windowsUri)) {
        html5ClientBaseUrl = windowsUri.substring(0, windowsUri.lastIndexOf('/'));
    }

    return {
        nativeClientDownloadUrl: getDownloadUrl(),
        html5ClientBaseUrl,
    };
}

function parseICA(icaText: string) {
    const lines = icaText.split('\n');
    const result = {};
    for (const line of lines) {
        const param = line.split('=');
        // FIXME: what if param.length > 2?
        if (param.length === 2) {
            result[param[0]] = param[1].trim();
        }
    }
    return result;
}

function launchCitrixApp(appToLaunch: CitrixModel, frameName: string, url: string, autoLaunch: boolean) {
    const launchFrame = document.getElementById(frameName) as HTMLIFrameElement;
    const doc = getDocument(launchFrame)!;

    if (document.all) {
        // IE only
        const code =
            '<object id="icaobj" classid="clsid:238f6f83-b8b4-11cf-8771-00a024541ee3">' +
            '<param name="Launch" value= "true">' +
            '<param name="Start" value= "true">' +
            '<param name="IPCLaunch" value="false">' +
            '<param name="ICAfile"  value=' +
            url +
            '>' +
            '</object>';

        doc.open();
        doc.write(code);
        doc.close();

        const icaObj: any = doc.getElementById('icaobj');

        const activeXObjectSucceeded = !(
            typeof icaObj === 'undefined' ||
            icaObj == null ||
            typeof icaObj.ClientVersion === 'undefined' ||
            !icaObj.Launch
        );
        if (activeXObjectSucceeded) {
            return;
        } else if (autoLaunch) {
            throw new Error(VdiErrorType.AUTO_LAUNCH_IE);
        }
    }
    window.open(url, frameName);
}

function isLaunchTimedOut(startTime: number) {
    return Date.now() - startTime >= NETWORK_REQUEST_TIMEOUT;
}

function retrieveLaunchResult(launchCookie: string) {
    const cookieValue = getCookie(launchCookie);
    if (cookieValue != null) {
        deleteCookie(launchCookie, '/', true);
    }
    return cookieValue;
}

function waitForAppLaunchResult(appToLaunch: CitrixModel) {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
        const timer = setInterval(() => {
            const result = retrieveLaunchResult(appToLaunch.launchCookie!);
            const timedOut = isLaunchTimedOut(startTime);
            if (result || timedOut) {
                clearInterval(timer);
                if (result === 'OK') {
                    resolve(result);
                } else {
                    reject(new VdiError(VdiErrorType.APP_LAUNCH, timedOut ? 'Timeout' : result));
                }
            }
        }, 100);
    });
}

function launchHtml5Client(appToLaunch: CitrixModel, html5BaseUri: string, launchType?: LaunchType) {
    const url =
        launchType === LaunchType.RESTART && appToLaunch.restartUri ? appToLaunch.restartUri : appToLaunch.launchUri!;
    return request(url, { timeout: NETWORK_REQUEST_TIMEOUT })
        .then((xhr) => {
            const icaText = xhr.responseText;
            const parsedIca = parseICA(icaText);
            const curTime = new Date().getTime();
            const windowName = 'html5' + String(Math.random()).replace(/\./, '');
            const html5Window = window.open(
                `${html5BaseUri}/HTML5Client/src/SessionWindow.html?launchid=${curTime}`,
                windowName
            );
            window.html5LaunchData[curTime] = parsedIca;
            html5Window!.icaDwldComplete = true;
        })
        .catch((err) => {
            const detail = retrieveLaunchResult(appToLaunch.launchCookie!);
            if (detail) {
                throw new VdiError(VdiErrorType.APP_LAUNCH, detail);
            }
            throw err;
        });
}

function launchNativeClient(appToLaunch: CitrixModel, launchType?: LaunchType) {
    const url =
        launchType === LaunchType.RESTART && appToLaunch.restartUri ? appToLaunch.restartUri : appToLaunch.launchUri!;
    const frameName = createFrame(appToLaunch.rdType);
    launchCitrixApp(appToLaunch, frameName, url, launchType === LaunchType.AUTOLAUNCH);
    return waitForAppLaunchResult(appToLaunch);
}

export { getClients as default, fetchCitrixPackages, launchHtml5Client, launchNativeClient };
